import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedDate } from 'react-intl';
import { Link } from 'react-router';

import { GhostButton } from '@bibliocommons/base-buttons';
import { Dropdown } from '@bibliocommons/base-dropdown';
import { Edit as IconEdit, Plus as IconPlus, Spinner } from '@bibliocommons/base-icons';
import { Link as BaseLink } from '@bibliocommons/base-links';

import Heading from 'app/components/shared/Heading';
import ContextMenu from 'app/components/admin/browse/shared/ContextMenu';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';
import { LOCALE_CODE } from 'app/constants/language';
import { browseHomePageSummaryShape } from 'app/shapes/admin/browse/home';
import { makeHomeCreatePageRoute, makeHomeEditPageRoute } from 'app/helpers/admin/browse/home';

import './AdminBrowseHomeListPage.scss';

const AdminBrowseHomeListPage = ({
  activeBrowseHomePageId,
  browseHomePages,
  isFetching,
  isSaving,
  localeCode,
  localeDropdownOptions,
  mapLocaleCodeToLabel,
  onActivate,
  onDelete,
  onDuplicate,
  onLocaleCodeChange,
  onPreview,
  onViewActiveHomePage
}) => {
  const createPageRoute = makeHomeCreatePageRoute(localeCode);
  const hasNoResults = !isFetching && browseHomePages.length === 0;

  return (
    <div className="cp-admin-browse-home-list-page container">
      <div className="heading-wrapper">
        <Heading className="heading" size="large" tagName="h1" variant>
          Browse: Home Editor
        </Heading>
        <div className="locale-selector">
          <span className="locale-label">Language</span>
          <Dropdown
            dataKey="DropdownLocaleCode"
            disabled={isSaving}
            handleSelect={onLocaleCodeChange}
            options={localeDropdownOptions}
          />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableHeading>Name</TableHeading>
          <TableHeading>Creator</TableHeading>
          <TableHeading>Last updated</TableHeading>
          <TableHeading>Status</TableHeading>
          <TableHeading />
        </TableHeader>
        {!isFetching && (
          <TableBody>
            {browseHomePages.map(browseHomePage => {
              const { browseHomePageId, createdBy, name, updatedAt, updatedBy } = browseHomePage;

              const isActive = activeBrowseHomePageId === browseHomePageId;

              const confirmDeleteModalMessage = `Are you sure you want to delete the page ‘${name}’?`;
              const editPageRoute = makeHomeEditPageRoute(localeCode, browseHomePageId);

              return (
                <TableRow key={browseHomePageId}>
                  <TableCell className="home-page-name">{name}</TableCell>
                  <TableCell>{createdBy}</TableCell>
                  <TableCell>
                    <FormattedDate day="2-digit" month="long" value={updatedAt} year="numeric" /> by {updatedBy}
                  </TableCell>
                  <TableCell>
                    <span className="actions">
                      <span>{isActive ? 'Active' : 'Draft'}</span>
                      {!isActive && (
                        <GhostButton
                          className="action"
                          dataKey="action-activate"
                          handleClick={() => onActivate(browseHomePageId)}
                          isLoading={isSaving}
                        >
                          Activate
                        </GhostButton>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="actions">
                      <Link className="action action--link-icon" to={editPageRoute}>
                        <IconEdit />
                      </Link>
                      <ContextMenu
                        className="action"
                        confirmDeleteModalHeading="Delete home page"
                        confirmDeleteModalMessage={confirmDeleteModalMessage}
                        onDeleteClick={isActive ? null : () => onDelete(browseHomePageId)}
                        onDuplicateClick={() => onDuplicate(browseHomePageId)}
                        onEditClick={null}
                        onPreviewClick={isActive ? null : () => onPreview(browseHomePageId)}
                        onViewClick={isActive ? onViewActiveHomePage : null}
                      />
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        )}
      </Table>
      {isFetching && (
        <div className="fetching-wrapper">
          <Spinner />
        </div>
      )}

      {hasNoResults && (
        <div className="no-results">
          <Heading size="medium" tagName="h2">
            No {mapLocaleCodeToLabel(localeCode)} home pages created yet.
          </Heading>
          <p>Click on the create new home page button below to begin creating a home page.</p>
        </div>
      )}

      <BaseLink className="create-new" dataKey="link-create-new" href={createPageRoute}>
        <IconPlus className="icon-plus" />
        Create new home page
      </BaseLink>
    </div>
  );
};

AdminBrowseHomeListPage.propTypes = {
  activeBrowseHomePageId: PropTypes.string,
  browseHomePages: PropTypes.arrayOf(browseHomePageSummaryShape).isRequired,
  isFetching: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired,
  localeDropdownOptions: ImmutablePropTypes.listOf(
    ImmutablePropTypes.mapContains({
      disabled: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      value: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired
    })
  ).isRequired,
  mapLocaleCodeToLabel: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onLocaleCodeChange: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onViewActiveHomePage: PropTypes.func.isRequired
};

export default AdminBrowseHomeListPage;
